import * as React from "react";
import { connect } from "react-redux";
import { Table } from "react-bootstrap";
import { Button, Select, SelectItem } from "carbon-components-react";
import { Save16 } from "@carbon/icons-react";

import { DocTypes } from "@eave/shared/types/api";
import { TaxYearsToUse } from "@eave/shared/types/api/income";
import HurdleView from "underwriter_dashboard/shared/HurdleView";

import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import Money from "../../Money";
import APIClient from "../../APIClient";
import EditableField from "../shared/EditableField";
import { loadApp } from "../actions";
import LFWSInput from "underwriter_dashboard/LFWSInput";

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");
const titleize = require("underscore.string/titleize");

require(`../../../assets/stylesheets/components/underwriter_dashboard/hurdle_editors/\
income_summary_tax.scss`);

interface HurdleEditorProps extends HurdleProps {
  dispatch?: Function;
}

const CALCULATION_METHOD_OPTIONS = [
  {
    label: "Smallest of last 12 months or last 24 months",
    value: "12Or24Months",
  },
  { label: "Last 12 months", value: "12Months" },
  { label: "Last 24 months", value: "24Months" },
];

// if qualification_year has changed since year_1_override was set,
// year_1_override may be invalid (ie not qualification_year - 1 or qualification_year - 2),
// in which case display the current value, and Ops can just
// select a different, valid one in the dropdown.
const taxReturnYearOptions = (qualification_year, year1) => {
  let year1Options = [qualification_year - 1, qualification_year - 2];
  if (!year1Options.includes(year1)) {
    year1Options = [year1, ...year1Options];
  }

  return year1Options.map(o => ({
    text: o,
    value: o,
  }));
};

class IncomeSummaryTaxHurdleEditor extends React.Component<HurdleEditorProps, any> {
  constructor(props) {
    super(props);
    this.state = {
      modifiedAggregations: Object.create(null),
    };
  }

  updateAggregationRules() {
    APIClient.put(
      `/${this.props.app.id}/income_aggregations`,
      (Object as any).values(this.state.modifiedAggregations),
    ).then(() => loadApp(this.props.dispatch, this.props.app.id));
  }

  onSave() {
    if ((Object as any).values(this.state.modifiedAggregations).length !== 0) {
      this.updateAggregationRules();
    }
  }

  static getCompositeKey(aggregation) {
    if (aggregation.sub_key === "") {
      return aggregation.key;
    }
    return `${aggregation.key}::${aggregation.sub_key}`;
  }

  handleAggregationChange(propsAggregationRule, field) {
    const newState = this.state;

    const compositeKey = IncomeSummaryTaxHurdleEditor.getCompositeKey(propsAggregationRule);
    let modifiedAggregation = newState.modifiedAggregations[compositeKey];
    if (modifiedAggregation === undefined) {
      // save a copy
      modifiedAggregation = JSON.parse(JSON.stringify(propsAggregationRule));
      newState.modifiedAggregations[compositeKey] = modifiedAggregation;
    }

    // decimal/nulldecimal fields cannot be empty, convert to null
    const nullDecimalFields = ["gross_up_override", "other_year_1", "other_year_2"];
    const isEmptyDecimal = nullDecimalFields.includes(field.name) && field.value === "";

    if (field.value === null || isEmptyDecimal) {
      // null field value means a reset
      modifiedAggregation[field.name] = propsAggregationRule[field.name];
    } else if (
      field.name === "gross_up_override" &&
      (Number(field.value) === 0 || Number(field.value) > 50)
    ) {
      // TODO add validation/constraint of max 50% while editing, reset for now
      // zero means remove override for gross up
      modifiedAggregation[field.name] = null;
    } else {
      // the value can be false for checkbox
      modifiedAggregation[field.name] = field.value;
    }

    this.setState(newState);
  }

  // for those income streams that have multiple sources, only 1 of which should be used at a time
  static alternateAggregationKeys(aggregation) {
    const soleProprietorshipSet = {
      sole_proprietorship: true,
      sole_proprietorship_ytd_p_and_l: true,
    };
    const reoSet = {
      rental_real_estate: true,
      rental_real_estate_lease: true,
    };

    const alternateAggregationSets = [soleProprietorshipSet, reoSet];
    const matchingAlternateAggregationSet = alternateAggregationSets.find(s => s[aggregation.key]);
    return (
      matchingAlternateAggregationSet &&
      Object.keys(matchingAlternateAggregationSet).filter(agg => agg !== aggregation.key)
    );
  }

  setAlternateAggregationsValues(alternateAggregationKeys, section, subKey, name, value) {
    alternateAggregationKeys.forEach(key => {
      const targetCompositeKey = `${key}::${subKey}`;

      // if there's a subsection for this composite key, set its value

      const targetSubsections = section.subsections.filter(
        ss => IncomeSummaryTaxHurdleEditor.getCompositeKey(ss.aggregation) === targetCompositeKey,
      );

      if (targetSubsections.length === 0) {
        return;
      }

      const targetAggregation = targetSubsections[0].aggregation;
      this.setSubsectionValue(targetAggregation, name, value);
    });
  }

  handleIncludeInRatios(aggregation, section, ev) {
    const includingInRatios = this.toggleSubsectionValue(aggregation, ev.target.name);

    // if including in ratios, then alternates (eg a lease vs a schedule E, for REO) should not be included.
    // however, the opposite isn't necessarily true.
    // if Ops is marking the current aggregation as not include in ratios, don't mark the alternate as included,
    // since then Ops would have no way of not including the income entirely (from any source)
    if (!includingInRatios) {
      return;
    }

    const alternateAggregationKeys = IncomeSummaryTaxHurdleEditor.alternateAggregationKeys(
      aggregation,
    );
    if (alternateAggregationKeys !== null) {
      this.setAlternateAggregationsValues(
        alternateAggregationKeys,
        section,
        aggregation.sub_key,
        ev.target.name,
        !includingInRatios,
      );
    }
  }

  setSubsectionValue(propsAggregationRule, fieldName, value) {
    const field = {
      name: fieldName,
      value,
    };
    this.handleAggregationChange(propsAggregationRule, field);
  }

  toggleSubsectionValue(propsAggregationRule, fieldName) {
    const compositeKey = IncomeSummaryTaxHurdleEditor.getCompositeKey(propsAggregationRule);
    const aggregation = this.state.modifiedAggregations[compositeKey] || propsAggregationRule;
    const value = !aggregation[fieldName];
    this.setSubsectionValue(propsAggregationRule, fieldName, value);
    return value;
  }

  renderTaxIncomeSummaryTable() {
    const { income_summary }: any = this.props.app;
    return (
      <Table className="tax-return tax-return--summary">
        <thead className="tax-return__header">
          <tr>
            <th className="tax-return__header__cell">Tax Return Totals</th>
            <th className="tax-return__header__cell" />
          </tr>
        </thead>
        <tbody className="tax-return__body">
          {income_summary.tax_summary.sections.map((section, i) => (
            <tr key={i}>
              <td>{section.heading}</td>
              <td>
                <Money value={section.monthly_total} />
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="tax-return__footer">
          <tr>
            <td className="tax-return__footer__cell">Grand Monthly Total</td>
            <td className="tax-return__footer__cell">
              <Money value={income_summary.tax_summary.grand_monthly_total} />
            </td>
          </tr>
        </tfoot>
      </Table>
    );
  }

  // for readability, sort subsections by sub key first, then key -- since sub key refers to the specific income stream,
  // whereas key refers to the income stream type source. eg for REO, keys refer to schedule E income, or lease income
  // sub keys refer to the specific property. so, sorting by sub key displays all of the data for 1 REO next to each other.
  static compareSubsections(a, b) {
    if (
      a.aggregation.sub_key < b.aggregation.sub_key ||
      (a.aggregation.sub_key === b.aggregation.sub_key && a.aggregation.key < b.aggregation.key)
    ) {
      return -1;
    }
    if (
      a.aggregation.sub_key === b.aggregation.sub_key &&
      a.aggregation.key === b.aggregation.key
    ) {
      return 0;
    }
    return 1;
  }

  renderSectionTable(section, qualification_year) {
    const hasSingleSubsection = section.subsections.length === 1;
    section.subsections.sort(IncomeSummaryTaxHurdleEditor.compareSubsections);

    // most recent year (year2) is shown first
    return (
      <Table key={section.section_key} className="tax-return tax-return--subsection">
        <thead className="tax-return__header">
          <tr>
            <th>{section.heading}</th>
          </tr>
        </thead>
        {section.subsections.map(subsection =>
          this.renderSubSectionRows(section, subsection, hasSingleSubsection, qualification_year),
        )}
        <tfoot className="tax-return__footer">
          <tr>
            <td />
            <td>Monthly Total:</td>
            <td>
              <Money value={section.monthly_total} />
            </td>
          </tr>
        </tfoot>
      </Table>
    );
  }

  static renderAggregationValueElement(fieldAggregation, value) {
    if (fieldAggregation.type === "money") {
      return <Money value={value} />;
    }
    if (fieldAggregation.type === "number") {
      return value;
    }
    return fieldAggregation.fixed_value;
  }

  static renderOperationSymbol(operation) {
    if (operation === "add") {
      return " (+)";
    }
    if (operation === "subtract") {
      return " (-)";
    }
    return "";
  }

  static renderOperation(fieldAggregation) {
    if (
      (fieldAggregation.type === "money" || fieldAggregation.type === "number") &&
      !fieldAggregation.excluded_from_total
    ) {
      return IncomeSummaryTaxHurdleEditor.renderOperationSymbol(fieldAggregation.operation);
    }
    return "";
  }

  static renderFieldTextCell(fieldAggregation) {
    const fieldTextClassName =
      fieldAggregation.operation === "none" || fieldAggregation.excluded_from_total
        ? "tax-return__info"
        : "";
    let referenceText = fieldAggregation.reference_text;
    if (referenceText !== "") {
      referenceText = ` (${referenceText})`;
    }
    const section = fieldAggregation.section.replace("_", " ");
    const sectionText = ` (${titleize(section)})`;
    const operationText = IncomeSummaryTaxHurdleEditor.renderOperation(fieldAggregation);
    return (
      <td className={fieldTextClassName}>
        {fieldAggregation.display_text}
        {referenceText}
        {sectionText}
        {operationText}
      </td>
    );
  }

  static borrowerEntityToDisplay(borrowerEntityKey) {
    if (!borrowerEntityKey) {
      return "Borrower (not ingested)";
    }
    if (borrowerEntityKey === 3) {
      return "Both borrowers";
    }
    return `Borrower ${borrowerEntityKey}`;
  }

  reoIDToDisplayAddress(reoID) {
    const {
      app: { owned_real_estate },
    } = this.props;
    const reo = owned_real_estate.find((r: any) => r.id === reoID);
    return reo && reo.address_line1;
  }

  renderSubSectionRows(section, subsection, hasSingleSubsection, qualification_year) {
    const compositeKey = IncomeSummaryTaxHurdleEditor.getCompositeKey(subsection.aggregation);
    const aggregationToUse =
      this.state.modifiedAggregations[compositeKey] || subsection.aggregation;

    const grossUp = subsection.aggregation.show_gross_up;
    const grossedUpText = grossUp ? " (grossed up)" : "";
    const showOther = subsection.aggregation.show_other;
    const isProfitAndLoss =
      subsection.aggregation.document_type === DocTypes.ProfitAndLossStatement;
    const isREOLease = subsection.aggregation.document_type === "real_estate_lease";
    const isTaxReturn = subsection.aggregation.document_type === "tax_return";
    const hasSubKey = subsection.aggregation.sub_key !== "";
    const subKeyField = subsection.aggregation.sub_key_field;
    const subKeys = subsection.aggregation.sub_key.split("::");
    const borrowerEntity = subsection.aggregation.borrower_entity;

    const showAggregation = IncomeSummaryTaxHurdleEditor.renderAggregationValueElement;

    const taxReturnSubsectionKeyToHeading = {
      rental_real_estate: "Schedule E (Form 1040)",
    };

    return (
      <tbody key={compositeKey}>
        {isProfitAndLoss && (
          <tr key={`${compositeKey}_pandl`}>
            <td className="tax-return__profit-loss" colSpan={3}>
              <h4>
                Year-To-Date Profit and Loss for
                {IncomeSummaryTaxHurdleEditor.borrowerEntityToDisplay(borrowerEntity)}; EIN{" "}
                {subKeys[1] || "(not ingested)"}
              </h4>
            </td>
          </tr>
        )}
        {isREOLease && (
          <tr key={`${compositeKey}_reo`}>
            <td className="tax-return__reo-lease" colSpan={3}>
              <h4>Rental Lease</h4>
              Only allowed if borrower has owned property at least 6 months
              <br />
              Property: {this.reoIDToDisplayAddress(+subKeys[0])}
            </td>
          </tr>
        )}
        {isTaxReturn && (
          <tr key={`${compositeKey}_taxReturnHeading`}>
            <td className="tax-return__profit-loss">
              {taxReturnSubsectionKeyToHeading[subsection.aggregation.key] && (
                <h4>{taxReturnSubsectionKeyToHeading[subsection.aggregation.key]}</h4>
              )}
              {hasSubKey && subKeyField === "ein" && <h4>EIN {subKeys[0] || "(not ingested)"}</h4>}
              {hasSubKey && subKeyField === "borrower_entity" && (
                <h4>{IncomeSummaryTaxHurdleEditor.borrowerEntityToDisplay(borrowerEntity)}</h4>
              )}
              {hasSubKey && subKeyField === "borrower_entity::ein" && (
                <h4>
                  {IncomeSummaryTaxHurdleEditor.borrowerEntityToDisplay(borrowerEntity)}; EIN{" "}
                  {subKeys[1] || "(not ingested)"}
                </h4>
              )}
            </td>
            <td>
              <Select
                id={`${compositeKey}_taxReturnYearsChooser`}
                defaultValue={aggregationToUse.year_1}
                onChange={e => {
                  const field = {
                    name: "year_1_override",
                    value: parseInt(e.target.value, 10),
                  };
                  this.handleAggregationChange(aggregationToUse, field);
                }}
                labelText=""
                inline
                light
              >
                {taxReturnYearOptions(qualification_year, aggregationToUse.year_1).map(o => (
                  <SelectItem
                    key={`${compositeKey}_taxReturnYearsChooser_${o.value}`}
                    text={`${o.text}`}
                    value={o.value}
                  />
                ))}
              </Select>
            </td>

            {/* to match SelectItem styles */}
            <td style={{ fontSize: "0.875rem", color: "#161616", verticalAlign: "middle" }}>
              {aggregationToUse.year_2}
            </td>
          </tr>
        )}
        {subsection.field_aggregations &&
          subsection.field_aggregations.map((fieldAggregation, i) => {
            if (fieldAggregation.display_text === "") {
              return null;
            }
            if (isProfitAndLoss) {
              return (
                <tr key={`${compositeKey}_${i}`}>
                  {IncomeSummaryTaxHurdleEditor.renderFieldTextCell(fieldAggregation)}
                  <td />
                  <td>
                    {showAggregation(fieldAggregation, fieldAggregation.ytd_aggregated_value)}
                  </td>
                </tr>
              );
            }
            if (isREOLease) {
              return (
                <tr key={`${compositeKey}_${i}`}>
                  {IncomeSummaryTaxHurdleEditor.renderFieldTextCell(fieldAggregation)}
                  <td>
                    {showAggregation(fieldAggregation, fieldAggregation.monthly_aggregated_value)}
                  </td>
                </tr>
              );
            }
            return (
              <tr key={`${compositeKey}_${i}`}>
                {IncomeSummaryTaxHurdleEditor.renderFieldTextCell(fieldAggregation)}
                <td>
                  {showAggregation(
                    fieldAggregation,
                    fieldAggregation.year_minus_one_aggregated_value,
                  )}
                </td>
                <td>
                  {showAggregation(
                    fieldAggregation,
                    fieldAggregation.year_minus_two_aggregated_value,
                  )}
                </td>
              </tr>
            );
          })}
        {showOther && (
          <tr key={`${compositeKey}_other`}>
            <td>Other</td>
            <td>
              $
              <EditableField
                fieldName="other_year_2"
                inputName={`other_year_2_${compositeKey}`}
                inputType="number"
                originalValue={subsection.aggregation.other_year_2 || 0}
                changedValue={aggregationToUse.other_year_2 || 0}
                handleOnChange={this.handleAggregationChange.bind(this, subsection.aggregation)}
              />
            </td>
            <td>
              $
              <EditableField
                fieldName="other_year_1"
                inputName={`other_year_1_${compositeKey}`}
                inputType="number"
                originalValue={subsection.aggregation.other_year_1 || 0}
                changedValue={aggregationToUse.other_year_1 || 0}
                handleOnChange={this.handleAggregationChange.bind(this, subsection.aggregation)}
              />
            </td>
          </tr>
        )}
        {subsection.override_values.map(v => (
          <tr>
            <td>
              {v.name} {IncomeSummaryTaxHurdleEditor.renderOperationSymbol(v.operation)}
            </td>
            <td>
              <Money value={v.value} />
            </td>
          </tr>
        ))}
        <tr key={`${compositeKey}_notes`}>
          <td>
            Notes:&nbsp;
            <EditableField
              fieldName="notes"
              inputName={`notes_${compositeKey}`}
              inputType="textarea"
              originalValue={subsection.aggregation.notes || ""}
              changedValue={aggregationToUse.notes || ""}
              handleOnChange={this.handleAggregationChange.bind(this, subsection.aggregation)}
            />
          </td>
          <td />
          <td>
            {grossUp && (
              <div>
                Gross up by&nbsp;
                <EditableField
                  fieldName="gross_up_override"
                  inputName={`gross_up_override_${compositeKey}`}
                  inputType="number"
                  originalValue={
                    subsection.aggregation.gross_up_override ||
                    subsection.calculated_gross_up_percent ||
                    0
                  }
                  changedValue={
                    aggregationToUse.gross_up_override ||
                    subsection.calculated_gross_up_percent ||
                    0
                  }
                  handleOnChange={this.handleAggregationChange.bind(this, subsection.aggregation)}
                />
                %
              </div>
            )}
          </td>
        </tr>
        <tr key={`${compositeKey}_total`}>
          {isProfitAndLoss && <td>Divided by Total Months in P&amp;L</td>}
          {isTaxReturn && (
            <td>
              <EditableField
                inputType="select"
                inputName={`calculation_method_${compositeKey}`}
                fieldName="calculation_method"
                originalValue={subsection.aggregation.calculation_method}
                changedValue={aggregationToUse.calculation_method}
                options={CALCULATION_METHOD_OPTIONS}
                handleOnChange={this.handleAggregationChange.bind(this, subsection.aggregation)}
              />
            </td>
          )}
          <td>
            {!hasSingleSubsection && (
              <span>
                Monthly subtotal: <Money value={subsection.monthly_total} />
                {grossedUpText}
              </span>
            )}
          </td>
          <td>
            <input
              name="include_in_ratios"
              type="checkbox"
              onChange={this.handleIncludeInRatios.bind(this, subsection.aggregation, section)}
              checked={aggregationToUse.include_in_ratios}
            />
            <label htmlFor="include_in_ratios">Include in ratios</label>
          </td>
        </tr>
      </tbody>
    );
  }

  render() {
    const { onHide, app, hurdle, hideTitle } = this.props;
    const { income_summary }: any = app;

    const displayValue = (formUpdates, instance): string => {
      // If formUpdates contain a valid value, it means there was an update, so show it
      if (formUpdates) {
        return formUpdates.tax_years_to_use;
      }

      // If the value exists as null, select our default
      if (instance && instance.tax_years_to_use === null) {
        return TaxYearsToUse.docsData;
      }

      // Otherwise, show the regular instance value by default
      if (instance) {
        return instance.tax_years_to_use;
      }

      throw new Error("No valid values to display for tax_years_to_use");
    };

    return (
      <HurdleView app={app} hurdle={hurdle} onHide={onHide} hideTitle={hideTitle}>
        <div className="dashboard-container income-summary-tax">
          <h3 style={{ marginBottom: "8px" }}>Review Tax Income</h3>

          <LFWSInput app={app}>
            {({ ws, formUpdates, handleChange, updateWorksheet }) => {
              const { instance } = ws;

              const noChanges =
                !formUpdates ||
                !instance ||
                formUpdates.tax_years_to_use === instance.tax_years_to_use;

              return (
                <div
                  style={{
                    position: "absolute",
                    top: "15px",
                    left: "280px",
                    display: "flex",
                    flexFlow: "row nowrap",
                  }}
                >
                  <Select
                    id="tax_years_to_use"
                    value={displayValue(formUpdates, instance)}
                    onChange={e => {
                      handleChange("tax_years_to_use", e.target.value);
                    }}
                    invalidText="A valid value is required"
                    labelText="Tax years to use"
                    inline
                    light
                    className={styles.taxYearsSelect}
                  >
                    {[
                      {
                        text: "Qual year - 1 and - 2",
                        value: TaxYearsToUse.minus1And2,
                      },
                      {
                        text: "Qual year - 2 and - 3",
                        value: TaxYearsToUse.minus2And3,
                      },
                      {
                        text: "Data per income stream",
                        value: TaxYearsToUse.docsData,
                      },
                    ].map(o => (
                      <SelectItem key={`tax-years-${o.text}`} text={o.text} value={o.value} />
                    ))}
                  </Select>
                  <Button
                    disabled={noChanges}
                    kind="tertiary"
                    size="field"
                    onClick={updateWorksheet}
                  >
                    Save
                  </Button>
                </div>
              );
            }}
          </LFWSInput>

          {this.renderTaxIncomeSummaryTable()}
          {income_summary.tax_summary.sections.map(section =>
            this.renderSectionTable(section, income_summary.qualification_year),
          )}

          <footer className={styles.footer}>
            <Button kind="secondary" onClick={onHide}>
              Cancel
            </Button>
            <Button kind="primary" onClick={this.onSave.bind(this)} renderIcon={Save16}>
              Save changes
            </Button>
          </footer>
        </div>
      </HurdleView>
    );
  }
}

export default connect()(IncomeSummaryTaxHurdleEditor);
